import React from 'react';

import { useLocation } from 'react-router-dom';

import { Logger } from '@mrkt/features/Platform';

import { Banner } from '@spotify-internal/encore-creator-web';

import { useViewport, Viewport } from '../../../../shared/lib/useViewport';
import { FormTitle } from '../components/FormTitle';
import { DialogFooter } from '../components/DialogFooter';
import { FormSuccess } from '../components/FormSuccess';
import { FormInput } from '../components/FormInput';
import { FormLoading } from '../components/FormLoading';
import { FormReview } from '../components/FormReview';
import { Provider, Org } from '../types';
import { usePut } from './usePut';
import { useGoToCurrentArtistHome } from './useGoToCurrentArtistHome';
import { useGoToCurrentArtistProfile } from './useGoToCurrentArtistProfile';
import { validateLinkForProviderAndOrg, validate } from './validations';
import { WizardStep, useWizard } from './useWizard';
import { useInput } from './useInput';
import { useHideBanner, useShowSuccess } from './useBanner';
import { NavStepper } from '../components/NavStepper';
import { unreachable } from './unreachable';
import { useArtistFundraisingPickSelfServeRollout } from '../../utils';
import { useOptimisticUpdate } from './useOptimisticUpdate';
import type { FundraisingLocationState } from '../types';

export function useFundraisingWizard() {
  const viewport = useViewport();

  const condensed = viewport === Viewport.XS;

  const [currWizardStep, wizardStepFromTo] = useWizard();

  const goToCurrentArtistProfile = useGoToCurrentArtistProfile();
  const goToCurrentArtistHome = useGoToCurrentArtistHome();

  const onExitForm = useArtistFundraisingPickSelfServeRollout()
    ? goToCurrentArtistProfile
    : goToCurrentArtistHome;

  const showSuccessBannerOnHome = useShowSuccess();

  const hideBannerOnHome = useHideBanner();

  const link = useInput('', '');

  const provider = useInput<Provider | ''>('');

  const org = useInput<Org | ''>('');

  const put = usePut();

  const optimisicUpdate = useOptimisticUpdate();

  const location = useLocation<FundraisingLocationState>();

  const isEditing = location.state?.isFundraisingFormEditMode;

  const Body = getBody(currWizardStep);

  const nextCTACopy = useNextCallToAction(currWizardStep);

  const nextWizardStep = getNextWizardStep(currWizardStep);

  const prevWizardStep = getPrevWizardStep(currWizardStep);

  const disableNext =
    !link.value || !link.dirty || currWizardStep === WizardStep.submitting;

  const hidePrev =
    currWizardStep === WizardStep.input ||
    currWizardStep === WizardStep.success ||
    currWizardStep === WizardStep.conflict;

  const disablePrev = currWizardStep === WizardStep.submitting;

  function stepIfValid() {
    validate(link, provider, org);

    const isValidLink =
      validateLinkForProviderAndOrg(provider, org)(link.value) == null;

    if (isValidLink) {
      wizardStepFromTo(currWizardStep, nextWizardStep);
    }
  }

  async function submitLinkAndProvider() {
    if (nextWizardStep && provider.value) {
      // Two steps forward submit -> submitting -> success
      const nextNextWizardStep = getNextWizardStep(nextWizardStep);

      // One step forward, one step back submit -> submitting -> failure
      const nextPrevWizardStep = getPrevWizardStep(nextWizardStep);

      // Step forward to submitting
      wizardStepFromTo(currWizardStep, nextWizardStep);

      const linkValue = link.value;
      const providerOrOrgValue = org.value || provider.value;

      const resp = await put(linkValue, providerOrOrgValue);

      if (resp?.ok) {
        // Step forward again to success
        wizardStepFromTo(nextWizardStep, nextNextWizardStep);

        // Optimistically update the artist profile
        optimisicUpdate({ channel: providerOrOrgValue, url: linkValue });

        // Update the banner state
        showSuccessBannerOnHome();
      } else if (resp?.status === 409) {
        // Step to already submitted error state
        wizardStepFromTo(nextWizardStep, WizardStep.conflict);

        // Don't show the banner again after conflict has occurred
        hideBannerOnHome();
      } else {
        // Log unexpected error code
        Logger.logError(
          'artist-fundraising-pick-put-error',
          new Error(
            `Unexpected response${
              resp?.status ? ` status ${resp?.status}` : ` ${resp}`
            }`,
          ),
        );
        // Step backward from submitting to failure
        wizardStepFromTo(nextWizardStep, nextPrevWizardStep);
      }
    }
  }

  return {
    banner: <Banner step={currWizardStep} />,
    dialogBody: (
      <Body isEditing={isEditing} link={link} provider={provider} org={org} />
    ),
    dialogBodyTitle:
      // Even though <FormTitle> returns null for WizardStep.success, the encore
      // <DialogFullScreen> component that this is ultimately passed to renders an
      // empty <h1> that takes up some space. To get <DialogFullScreen> to omit
      // the empty <h1> we pass null here instead of a component that returns null.
      currWizardStep === WizardStep.success ? null : (
        <FormTitle isEditing={isEditing} step={currWizardStep} />
      ),
    dialogFooter: (
      <DialogFooter
        isEditing={isEditing}
        prev={() => {
          wizardStepFromTo(currWizardStep, prevWizardStep);
        }}
        noPrev={disablePrev}
        hidePrev={hidePrev}
        next={async () => {
          switch (currWizardStep) {
            case WizardStep.input: {
              stepIfValid();
              break;
            }
            /* eslint-disable no-fallthrough */
            case WizardStep.submit:
            // Failure mode is spec'd as just a chance to resubmit with an
            // error banner
            case WizardStep.failure: {
              await submitLinkAndProvider();
              break;
            }
            case WizardStep.conflict:
            case WizardStep.success: {
              onExitForm();
              break;
            }
            case WizardStep.submitting:
              break;
            /* eslint-enable no-fallthrough */
            default:
              unreachable(currWizardStep);
              break;
          }
        }}
        noNext={disableNext}
        nextCopy={nextCTACopy ?? ''}
        step={currWizardStep}
      />
    ),
    dialogNavStepper: (
      <NavStepper
        isEditing={isEditing}
        step={currWizardStep}
        visible={!condensed}
      />
    ),
    dialogOnClose: onExitForm,
    dialogOnEscape: onExitForm,
    dialogTitle: 'COVID-19 support form',
  } as const;
}

function getBody(step: WizardStep) {
  switch (step) {
    case WizardStep.input:
      return FormInput;
    case WizardStep.failure:
    case WizardStep.submit:
      return FormReview;
    case WizardStep.submitting:
      return () => <FormLoading />;
    case WizardStep.success:
      return FormSuccess;
    case WizardStep.conflict:
      return () => null;
    default:
      unreachable(step);
      return () => null;
  }
}

function useNextCallToAction(step: WizardStep) {
  const viewport = useViewport();

  const condensed = viewport === Viewport.XS;

  switch (step) {
    case WizardStep.input:
      return 'next';
    case WizardStep.failure:
    case WizardStep.submit:
    case WizardStep.submitting:
      return condensed ? 'agree & submit' : 'agree and submit';
    case WizardStep.success:
    case WizardStep.conflict:
      return 'done';
    default:
      unreachable(step);
      return null;
  }
}

function getNextWizardStep(step: WizardStep) {
  switch (step) {
    case WizardStep.input:
      return WizardStep.submit;
    case WizardStep.failure:
    case WizardStep.submit:
      return WizardStep.submitting;
    case WizardStep.submitting:
      return WizardStep.success;
    case WizardStep.success:
    case WizardStep.conflict:
      return null;
    default:
      unreachable(step);
      return null;
  }
}

function getPrevWizardStep(step: WizardStep) {
  switch (step) {
    case WizardStep.input:
      return null;
    case WizardStep.failure:
    case WizardStep.submit:
      return WizardStep.input;
    case WizardStep.submitting:
      return WizardStep.failure;
    case WizardStep.success:
    case WizardStep.conflict:
      return null;
    default:
      unreachable(step);
      return null;
  }
}
